import { useRef, useCallback, useEffect, useState, useMemo } from "react";

import { useAnimationQueue } from "./useAnimationQueue";
import { useBackgroundShapesConfiguration } from "./useBackgroundShapesConfiguration";

import {
  animationConfig,
  AnimationConfig,
} from "@/features/FormV2/sharedComponents/BackgroundShapes/ThemedBackgroundShapes.animation-config";

export interface ThemedBackgroundShapeTransform {
  rotate: number;
  translateY: number;
  scale: number;
  opacity: number;
}

export const useBackgroundShapesState = () => {
  const shapes = useBackgroundShapesConfiguration();
  const shapeIds = useMemo(() => shapes.map((shape) => shape.id), [shapes]);

  // Initialize transformation states for all shapes
  const [transforms, setTransforms] = useState<{ [shapeId: string]: ThemedBackgroundShapeTransform }>({});
  const [transition, setTransition] = useState<string>("transform 200ms ease, opacity 200ms ease");

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const rotationIndexRef = useRef<number>(0);
  const isDroppedRef = useRef<boolean>(false); // Track if shapes are dropped

  const { enqueueAnimation, onAnimationComplete, isAnimatingRef } = useAnimationQueue({
    maxQueueLength: 1,
  });

  // Synchronize transforms state with shapes when size screen changes
  useEffect(() => {
    setTransforms((prevTransforms) => {
      const newTransforms: { [shapeId: string]: ThemedBackgroundShapeTransform } = { ...prevTransforms };

      let hasChanges = false;

      shapes.forEach((shape) => {
        if (!prevTransforms[shape.id]) {
          // Initialize new shape transform
          newTransforms[shape.id] = {
            rotate: 0,
            translateY: 0,
            scale: 1,
            opacity: 1,
          };
          hasChanges = true;
        }
      });

      // Remove transforms for shapes that no longer exist
      Object.keys(prevTransforms).forEach((id) => {
        if (!shapeIds.includes(id)) {
          delete newTransforms[id];
          hasChanges = true;
        }
      });

      return hasChanges ? newTransforms : prevTransforms;
    });
  }, [shapes, shapeIds]);

  // Clear active timeouts
  useEffect(() => {
    const timeouts = timeoutsRef.current;

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const executeAnimation = useCallback(
    (animation: AnimationConfig) => {
      // Mark that an animation sequence is currently running
      isAnimatingRef.current = true;
      const { steps } = animation;

      // Iterate over each animation step
      steps.forEach((step, index) => {
        // Calculate the cumulative delay for the current step by summing the durations of all previous steps
        const delay = steps
          .slice(0, index) // Get all steps before the current one
          .reduce((acc, curr) => acc + curr.duration, 0); // Sum their durations

        // Schedule the execution of the current step after the calculated delay
        const timeout = setTimeout(() => {
          // Update the CSS transition duration based on the current step's duration
          setTransition(`transform ${step.duration}ms ease, opacity ${step.duration}ms ease`);

          // Update the transformation states for all shapes
          setTransforms((prev) => {
            // Create a shallow copy of the previous transforms to avoid direct state mutation
            const updated = { ...prev };

            // Iterate over each shape by its ID
            shapeIds.forEach((id) => {
              // Find the corresponding shape configuration
              const shape = shapes.find((s) => s.id === id);
              if (!shape) {
                // If the shape is not found, skip to the next iteration
                return;
              }

              // Retrieve the previous transformation state for the shape or initialize it if not present
              const prevTransform = prev[id] || {
                rotate: 0,
                translateY: 0,
                scale: 1,
                opacity: 1,
              };

              // Apply the rotation multiplier specific to the shape
              const rotationMultiplier = shape.rotationMultiplier || 1;

              // Initialize the new scale based on the previous scale
              let scale = prevTransform.scale;

              // If the current step includes a scaling transformation, apply it
              if (step.scale) {
                // The scale function takes the shape's scale multiplier and returns the new scale
                scale *= step.scale(shape.scaleMultiplier || 1);
              }

              // Update the transformation state for the current shape
              updated[id] = {
                // Increment the rotation by the step's rotate value multiplied by the rotation multiplier
                rotate: prevTransform.rotate + (step.rotate || 0) * rotationMultiplier,
                // Increment the vertical translation by the step's translateY value
                translateY: prevTransform.translateY + (step.translateY || 0),
                scale,
                // Update the opacity if the step specifies it; otherwise, retain the previous opacity
                opacity: step.opacity !== undefined ? step.opacity : prevTransform.opacity,
              };
            });

            // Return the updated transformation states to update the component's state
            return updated;
          });

          // If this is the last step in the sequence, schedule the callback to indicate animation completion
          if (index === steps.length - 1) {
            // Schedule the onAnimationComplete callback after the current step's duration
            const finalTimeout = setTimeout(() => {
              onAnimationComplete();
            }, step.duration);

            // Store the timeout ID for potential cleanup
            timeoutsRef.current.push(finalTimeout);
          }
        }, delay); // Execute the step after the cumulative delay

        // Store the timeout ID for potential cleanup
        timeoutsRef.current.push(timeout);
      });
    },
    [isAnimatingRef, onAnimationComplete, shapeIds, shapes]
  );

  // Define the action functions
  const rotate = useCallback(() => {
    // If rotate from initial position, use rotateAndScale animation, otherwise use simpleRotate
    const animationKey = rotationIndexRef.current === 0 ? "rotateAndScale" : "simpleRotate";
    const animation = animationConfig[animationKey];
    rotationIndexRef.current += 1;
    const animate = () => executeAnimation(animation);
    enqueueAnimation(animate);
  }, [enqueueAnimation, executeAnimation]);

  const reverseRotate = useCallback(() => {
    // If reverseRotate to initial position, use reverseRotateAndScaleDown animation, otherwise use simpleReverseRotate
    const animationKey = rotationIndexRef.current === 1 ? "reverseRotateAndScaleDown" : "simpleReverseRotate";
    const animation = animationConfig[animationKey];
    rotationIndexRef.current -= 1;
    const animate = () => executeAnimation(animation);
    enqueueAnimation(animate);
  }, [enqueueAnimation, executeAnimation]);

  const drop = useCallback(() => {
    // Safety net: Allow drop only if shapes are in the "up" position
    if (isDroppedRef.current) {
      return;
    }
    isDroppedRef.current = true; // Update state to indicate shapes are dropped
    const animate = () => executeAnimation(animationConfig.drop);
    enqueueAnimation(animate);
  }, [enqueueAnimation, executeAnimation]);

  const up = useCallback(() => {
    // Safety net: Allow up only if shapes have been dropped
    if (!isDroppedRef.current) {
      return;
    }
    isDroppedRef.current = false; // Update state to indicate shapes are back up
    const animate = () => executeAnimation(animationConfig.up);
    enqueueAnimation(animate);
  }, [enqueueAnimation, executeAnimation]);

  return {
    rotate,
    drop,
    reverseRotate,
    up,
    shapesData: {
      shapes,
      transition,
      transforms,
    },
  };
};
